import { dbg } from './debugFlag'
// ─── hlBookFeed: the HL order-book data slice, one owner ───
//
// Everything HL-book lives here: the WebSocket to the capture service, the
// raw snapshot ring, ingest (arrival stamps, trim, session max, naked-wall
// detection), the FLOW deque, and the tf-keyed SLOT fold. Before this
// module, FPCC owned the WS + a slot fold and LiquidityHeatLayer ran a
// SECOND full fold with its own cursor over the same ring — two
// uncoordinated derivations of one dataset, flagged temporary the day it
// was built. Consumers now hold one book object:
//
//   FPCC:   book.watch(coin) from a thin effect; book.advanceSlots(tfMs,
//           key) at the top of the heat render; reads book.slot.* for the
//           frozen-column history, book.snaps.length for gating,
//           book.maxSz for the menu readout.
//   Layer:  reads book.flow (+ book.gen for reset detection). Nothing
//           else — flow maintenance moved to ingest, so the layer needs
//           zero fold calls.
//
// TWO FOLDS, ON PURPOSE — they are different jobs, not duplication:
//   ingest fold : O(1) per snapshot, tf-INDEPENDENT. Applies the
//                 window-replace rule to the running book the moment a
//                 snapshot arrives, pushes a compact copy to `flow`
//                 (arrival-stamped), feeds walls + maxSz. Never refolds.
//   slot fold   : tf-KEYED, cursor over the RAW ring with its own running
//                 book (ported verbatim from FPCC). Exists because slot
//                 boundaries depend on the chart's timeframe, which can
//                 change at any moment and force a refold — which is
//                 exactly why the raw ring is retained at all.
//
// THE CLOCK RULE (the trail's, verbatim philosophy): flow AGES use tsA —
// the arrival stamp, both ends client clock — while SLOT bucketing uses
// snap.ts — server, aligned with candles. Mixing ends once put ~15s of
// skew between the seam and the youngest band.
//
// Multi-chart: one book per chart instance (createHlBook in a ref), never
// a singleton — workspace slots can watch different coins simultaneously.

export const BOOK_WS_URL = import.meta.env.VITE_BOOK_WS_URL ||
  ((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.hostname + ':8767')

export const HEAT_RING_MAX = 6000        // 1600-snap 24h history + hours of 2Hz live

// Flow retention: superset of any drop-off the UI can ask for (knob caps at
// the trail's 30s window) + ingest margin. The layer clips to the knob.
const FLOW_RETAIN_MS = 40_000

export function createHlBook() {
  const book = {
    // ── shared state (consumers read fields; module is the only writer,
    //    except the layer's projection-cache annotations on flow entries:
    //    st.rr / st.rt / st.rk are layer-owned and documented there) ──
    snaps: [],                 // raw ring, ts-ascending
    gen: 0,                    // bumped on wholesale replace/clear — consumers reset derived state
    maxSz: 1,                  // session max single level (walls gate + menu readout)
    walls: new Map(),          // naked walls: px -> { px, sz, ts }
    flow: [],                  // per-snapshot compact running-book states { ts, tsA, px, tot, ask }
    // ingest-time running book (tf-independent)
    px: new Map(),             // price -> total size
    ask: new Map(),            // price -> ask size
    // tf-keyed slot fold — shape is byte-compatible with FPCC's old
    // heatFoldRef so the history render body did not have to change.
    slot: { cursor: 0, gen: -1, key: '', slotArr: [], stateArr: [], px: new Map(), ask: new Map(), lastSlot: -1 },
    watch,
    advanceSlots,
  }

  // `session` is bumped by every watch() teardown. Each socket captures the
  // session it was born into and every one of its handlers no-ops once
  // superseded — see connect() for why the `closed` flag alone was not enough.
  let ws = null, closed = true, retry = null, fails = 0, coin = null, session = 0
  let liveAnnounced = null

  const backoffMs = () => {
    fails++
    if (fails === 4) console.warn(`[BookHeat] cannot reach ${BOOK_WS_URL} — backing off (is :8767 fronted like :8765?)`)
    return Math.min(3000 * 2 ** Math.min(fails - 1, 4), 30000)
  }

  const resetData = () => {
    book.snaps = []
    book.maxSz = 1
    book.walls = new Map()
    book.flow = []
    book.px = new Map()
    book.ask = new Map()
    book.gen++
  }

  // ── window-replace, the ONE rule both folds share ──
  // A snapshot speaks only for the price window it observed: INSIDE its
  // own [min,max], present updates and absent means pulled; OUTSIDE, it
  // says nothing and the state carries.
  const applyWindowReplace = (pxMap, askMap, snap) => {
    let pLo = Infinity, pHi = -Infinity
    for (let k = 0; k < 2; k++) {
      const side = k === 0 ? snap.b : snap.a
      for (const lv of side) { const px = lv[0]; if (px < pLo) pLo = px; if (px > pHi) pHi = px }
    }
    if (pLo > pHi) return
    for (const px of pxMap.keys()) {
      if (px >= pLo && px <= pHi) { pxMap.delete(px); askMap.delete(px) }
    }
    for (let k = 0; k < 2; k++) {
      const side = k === 0 ? snap.b : snap.a
      for (const [px, sz] of side) {
        pxMap.set(px, (pxMap.get(px) || 0) + sz)
        if (k === 1) askMap.set(px, (askMap.get(px) || 0) + sz)
      }
    }
  }

  const ingest = (snap) => {
    // ARRIVAL stamp — see the clock rule in the header.
    snap.tsA = Date.now()
    book.snaps.push(snap)
    if (book.snaps.length > HEAT_RING_MAX) {
      const cut = book.snaps.length - HEAT_RING_MAX
      book.snaps.splice(0, cut)
      // Front-trim shifts every index left — the slot fold's cursor must
      // shift with it or it re-reads (or skips) snapshots. Frozen slot
      // states stay valid: derived truth, independent of ring indices.
      book.slot.cursor = Math.max(0, book.slot.cursor - cut)
    }
    let mx = book.maxSz
    for (const [, sz] of snap.b) if (sz > mx) mx = sz
    for (const [, sz] of snap.a) if (sz > mx) mx = sz
    book.maxSz = mx

    // ── ingest fold: running book + flow entry ──
    applyWindowReplace(book.px, book.ask, snap)
    {
      const n = book.px.size
      const pxA = new Float64Array(n), totA = new Float64Array(n), askA = new Float64Array(n)
      let j = 0
      for (const [px, tot] of book.px) { pxA[j] = px; totA[j] = tot; askA[j] = book.ask.get(px) || 0; j++ }
      book.flow.push({ ts: snap.ts, tsA: snap.tsA, px: pxA, tot: totA, ask: askA })
      const cutoff = snap.tsA - FLOW_RETAIN_MS
      while (book.flow.length && book.flow[0].tsA < cutoff) book.flow.shift()
    }

    // ─── NAKED WALLS: top-decile levels persist until TESTED ───
    // Any level in the session's top 10% (log scale, FIXED at 0.90 —
    // deliberately independent of the display slider) registers as a
    // wall. It outlives the book: pulled or left behind, the ghost
    // projects to the live edge. Price coming within 0.1% of mid (the
    // touch knob — the literal `0.001` below) counts as a test -> ghost
    // deleted; the live book owns that zone again. A wall re-printing
    // later simply re-registers.
    const walls = book.walls
    const bb = snap.b[0]?.[0], ba = snap.a[0]?.[0]
    if (bb && ba) {
      const mid = (bb + ba) / 2
      for (const [k, w] of walls) {
        if (Math.abs(mid - w.px) <= mid * 0.001) walls.delete(k)   // tested
      }
    }
    const lg = Math.log1p(book.maxSz || 1)
    for (const side of [snap.b, snap.a]) {
      for (const [px, sz] of side) {
        if (Math.log1p(sz) / lg < 0.90) continue
        const w = walls.get(px)
        if (!w || sz > w.sz) walls.set(px, { px, sz: Math.max(sz, w?.sz || 0), ts: snap.ts })
        else w.ts = snap.ts
      }
    }
    if (walls.size > 220) {           // cap: drop stalest
      const oldest = [...walls.entries()].sort((a, b) => a[1].ts - b[1].ts)
      for (let i = 0; i < walls.size - 200; i++) walls.delete(oldest[i][0])
    }
  }

  // ── SOCKET IDENTITY, not just a flag ──
  // Every handler below belongs to `sock` and to the session that created
  // it. The previous cut assigned them off the module-level `ws` and gated
  // the reconnect on `closed` alone, which leaked a live socket on EVERY
  // pair switch: FPCC's heat effect calls watch twice (cleanup, then body),
  // so the order is
  //   watch(null)  -> closed = true, socketA.close() [async], ws = null
  //   watch('BTC') -> closed = false, connect() -> ws = socketB
  //   socketA.onclose FINALLY fires -> sees closed === false -> reconnects
  //                -> ws = socketC, and socketB is orphaned OPEN forever.
  // Nothing ever closed an orphan (watch only closes the tracked `ws`), so
  // each switch stranded another socket holding a server-side book_sub. The
  // client looked fine because onmessage filters on `m.coin === coin` and
  // dropped the frames one line before ingest — bytes on the wire, rising
  // subscriber count on :8767, nothing on the chart.
  // Capturing the session fixes all three faces of it: a superseded onclose
  // cannot schedule a reconnect, a late onopen closes itself instead of
  // subscribing, and stray frames are refused at the handler rather than
  // relying on the coin filter.
  const connect = () => {
    if (closed) return
    const mySession = session
    let sock
    try { sock = new WebSocket(BOOK_WS_URL) } catch { retry = setTimeout(connect, backoffMs()); return }
    ws = sock
    sock.onopen = () => {
      // Superseded while CONNECTING: never subscribe, just hang up. Without
      // this the server would hold a book_sub no one will ever read.
      if (mySession !== session) { try { sock.close() } catch {} ; return }
      sock.send(JSON.stringify({ action: 'book_sub', coin, historyMin: 1440 }))   // full retention window
    }
    sock.onmessage = (ev) => {
      if (mySession !== session) return
      try {
        const m = JSON.parse(ev.data)
        if (m.type === 'book_history' && m.coin === coin) {
          resetData()   // rebuilt in-order by the replay (walls included)
          for (const sn of (m.snaps || [])) ingest(sn)
          dbg(`[BookHeat] history: ${book.snaps.length} snaps for ${coin} via ${BOOK_WS_URL}`)
        } else if (m.type === 'book' && m.coin === coin) {
          if (liveAnnounced !== coin) {
            liveAnnounced = coin
            dbg(`[BookHeat] live stream up for ${coin} (2Hz)`)
          }
          ingest({ ts: m.ts, b: m.b, a: m.a })
        }
      } catch {}
    }
    sock.onclose = () => {
      if (mySession !== session) return   // a previous session's socket — not ours to revive
      if (!closed) retry = setTimeout(connect, backoffMs())
    }
  }

  function watch(nextCoin) {
    if (nextCoin === coin && !closed === !!nextCoin) return   // no-op: already watching it
    // tear down the current session. The bump must come FIRST: it is what
    // makes the outgoing socket's late onclose/onopen no-ops even after
    // `closed` flips back to false for the incoming coin below.
    session++
    closed = true
    if (retry) { clearTimeout(retry); retry = null }
    try { ws?.send(JSON.stringify({ action: 'book_unsub', coin })) } catch {}
    try { ws?.close() } catch {}
    ws = null
    resetData()
    coin = nextCoin || null
    fails = 0
    liveAnnounced = null
    if (!coin) return
    closed = false
    connect()
  }

  // ── slot fold (ported verbatim from FPCC's FOLD THE TAIL) ──
  // Advances the tf-keyed fold over new ring entries; freezes the running
  // book as compact parallel arrays each time a slot closes. TF or coin
  // change resets via key; wholesale ring replacement resets via gen;
  // front-trims shift the cursor at the ingest site above.
  function advanceSlots(tfMs, fkey) {
    const F = book.slot
    if (F.key !== fkey || F.gen !== book.gen) {
      F.key = fkey; F.gen = book.gen
      F.cursor = 0; F.slotArr = []; F.stateArr = []
      F.px = new Map(); F.ask = new Map(); F.lastSlot = -1
    }
    const freeze = () => {
      // One allocation per CLOSED slot (amortized once per candle, never
      // per frame), iterated flat at draw with zero Map hashing.
      const n = F.px.size
      const pxA = new Float64Array(n), totA = new Float64Array(n), askA = new Float64Array(n)
      let j = 0
      for (const [px, tot] of F.px) { pxA[j] = px; totA[j] = tot; askA[j] = F.ask.get(px) || 0; j++ }
      F.slotArr.push(F.lastSlot)
      F.stateArr.push({ px: pxA, tot: totA, ask: askA })
      // Session cap: parallel shift is rare (once past 4000 closed slots)
      if (F.slotArr.length > 4000) { F.slotArr.splice(0, 500); F.stateArr.splice(0, 500) }
    }
    const snaps = book.snaps
    while (F.cursor < snaps.length) {
      const snap = snaps[F.cursor]
      const slot = Math.floor(snap.ts / tfMs) * tfMs
      if (slot !== F.lastSlot) {
        if (F.lastSlot >= 0) freeze()   // slot closed: freeze its final book
        F.lastSlot = slot
      }
      applyWindowReplace(F.px, F.ask, snap)
      F.cursor++
    }
  }

  return book
}